import React from 'react';
import { Typography, Tooltip, Box } from '@mui/material';

// Type definitions for cell values and error structures
type CellValue = string | number | Date | null | undefined;

interface AvailableInstrument {
  sourceId?: string | number | { sourceId?: string | number };
  name?: string;
  currency?: string;
  type?: string;
  description?: string;
}

interface CellErrorHighlightProps {
  value: CellValue;
  hasError: boolean;
  errorInfo?: {
    title: string;
    message: string;
    affected_field?: string;
    field_value?: string;
    available_instruments?: AvailableInstrument[];
  };
  fieldName?: string;
}

const CellErrorHighlight: React.FC<CellErrorHighlightProps> = ({
  value,
  hasError,
  errorInfo,
  fieldName,
}) => {
  if (hasError && errorInfo) {
    const hasMultipleInstruments =
      errorInfo.available_instruments &&
      errorInfo.available_instruments.length > 0;

    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tooltip
          title={
            <Box sx={{ maxWidth: 400 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: 'white', mb: 1 }}
              >
                ⚠️ {errorInfo.title}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'white' }}>
                {errorInfo.message}
              </Typography>
              {errorInfo.affected_field && (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: 'white', mb: 1 }}
                >
                  📍 Field: {errorInfo.affected_field}
                </Typography>
              )}
              {errorInfo.field_value && (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: 'white', mb: 1 }}
                >
                  🏷️ Value: {errorInfo.field_value}
                </Typography>
              )}

              {hasMultipleInstruments && (
                <>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: 'white', mt: 2, mb: 1 }}
                  >
                    📋 Available Instruments (
                    {errorInfo.available_instruments!.length}):
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {errorInfo.available_instruments!.map(
                      (instrument, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: 1,
                            p: 1,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderRadius: 1,
                            border: '1px solid rgba(255,255,255,0.2)',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'white' }}
                          >
                            {instrument.name || 'Unknown Name'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.8)' }}
                          >
                            ID:{' '}
                            {typeof instrument.sourceId === 'object'
                              ? instrument.sourceId?.sourceId
                              : instrument.sourceId || 'N/A'}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.8)', ml: 2 }}
                          >
                            Currency: {instrument.currency || 'N/A'}
                          </Typography>
                          {instrument.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.7)',
                                display: 'block',
                              }}
                            >
                              {instrument.description}
                            </Typography>
                          )}
                        </Box>
                      )
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      mt: 1,
                      display: 'block',
                    }}
                  >
                    💡 Use the exact sourceId or be more specific with your
                    symbol
                  </Typography>
                </>
              )}
            </Box>
          }
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'rgba(0,0,0,0.9)',
                maxWidth: 450,
              },
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: hasMultipleInstruments
                ? 'rgba(33, 150, 243, 0.1)'
                : 'rgba(244, 67, 54, 0.1)',
              border: hasMultipleInstruments
                ? '1px dashed #2196f3'
                : '1px dashed #f44336',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              animation: hasMultipleInstruments
                ? 'infoPulse 2s ease-in-out infinite'
                : 'errorPulse 2s ease-in-out infinite',
              '@keyframes errorPulse': {
                '0%': { borderColor: '#f44336' },
                '50%': { borderColor: '#ff9800' },
                '100%': { borderColor: '#f44336' },
              },
              '@keyframes infoPulse': {
                '0%': { borderColor: '#2196f3' },
                '50%': { borderColor: '#4caf50' },
                '100%': { borderColor: '#2196f3' },
              },
            }}
          >
            <Typography
              sx={{
                color: hasMultipleInstruments ? '#2196f3' : '#f44336',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {String(value || '')}
            </Typography>
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                backgroundColor: hasMultipleInstruments ? '#2196f3' : '#f44336',
                color: 'white',
                borderRadius: '50%',
                width: 18,
                height: 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                zIndex: 1,
              }}
            >
              {hasMultipleInstruments ? '?' : '!'}
            </Box>
          </Box>
        </Tooltip>
      </Box>
    );
  }

  // Normal cell rendering - display raw value as-is
  return (
    <Typography sx={{ fontSize: '0.875rem' }}>{String(value ?? '')}</Typography>
  );
};

export default CellErrorHighlight;
